import React, { useEffect, useState } from "react";
import { Card, Button } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  createTenant,
  createTenantUser,
  getTimeZone,
  mapUserWithTenant,
} from "../../../services/TenantsService";
import {
  publicTenantRegistration,
  publicTenantUserCreate,
} from "../../../services/publicService";
import Swal from "sweetalert2";
import PublicImageCropModal from "../../components/public-page/PublicImageCropModal";

const TenantRegistration = () => {
  const initialFormData = {
    // Tenant Details
    tenantName: "",
    shortName: "",
    companyEmail: "",
    companyPhone: "",
    address: "",
    landLine: "",
    contactName: "",
    timeZone: "",
    isLocation: false,

    // Admin User Details
    adminName: "",
    adminEmail: "",
    adminMobile: "",
    password: "",

    // Location
    latitude: "",
    longitude: "",
    radius: "",
  };
  const [loading, setLoading] = useState(false);
  const [timeZone, setTimeZone] = useState([]);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [uploadedImageFiles, setUploadedImageFiles] = useState([]);
  const [showImageCropModal, setShowImageCropModal] = useState(false);

  const locationOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const timeZoneOptions = timeZone.map((tz) => ({
    value: tz.timeZoneId,
    label: `(${tz.offset}) ${tz.timeZoneId} - ${tz.displayName} `,
  }));

  async function fetchTimeZone() {
    try {
      const res = await getTimeZone();
      setTimeZone(res);
      console.log(timeZone);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }

  useEffect(() => {
    fetchTimeZone();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateEmail = (email) => {
    return /^[\w-.+]+@([\w-]+\.)+[a-zA-Z]{2,}$/.test(email);
  };

  const validateAdminStep = () => {
    const newErrors = {};

    if (!formData.adminName.trim()) {
      newErrors.adminName = "Admin name is required";
    }
    if (!formData.adminMobile.trim()) {
      newErrors.adminMobile = "Mobile number is required";
    }
    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = "Admin email is required";
    } else if (!validateEmail(formData.adminEmail)) {
      newErrors.adminEmail = "Enter a valid email";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTenantStep = () => {
    const newErrors = {};

    if (!formData.tenantName.trim()) {
      newErrors.tenantName = "Academy name is required";
    }
    if (!formData.shortName.trim()) {
      newErrors.shortName = "Short name is required";
    }
    if (!formData.companyEmail.trim()) {
      newErrors.companyEmail = "Company email is required";
    } else if (!validateEmail(formData.companyEmail)) {
      newErrors.companyEmail = "Enter a valid email";
    }
    if (!formData.companyPhone.trim()) {
      newErrors.companyPhone = "Company phone is required";
    }
    if (!formData.contactName.trim()) {
      newErrors.contactName = "Contact name is required";
    }
    if (!formData.timeZone) {
      newErrors.timeZone = "Time zone is required";
    }
    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (!validateAdminStep()) return;

    try {
      setLoading(true);

      const payload = {
        name: formData.adminName,
        email: formData.adminEmail,
        phone: formData.adminMobile,
        landLine: formData.landLine,
        password: formData.password,
      };

      const user = await publicTenantUserCreate(payload);

      if (!user?.id) {
        throw new Error("User creation failed");
      }
      setCreatedUserId(user.klId);
      Swal.fire("User Created!", "User created successfully", "success");

      setStep(2);
    } catch (error) {
      const message =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Something went wrong";
      // toast.error(message);
      Swal.fire("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateTenantStep()) return;

    try {
      setLoading(true);
      const tenantPayload = {
        name: formData.tenantName,
        shortName: formData.shortName,
        email: formData.companyEmail,
        phone: formData.companyPhone,
        address: formData.address,
        landLine: formData.landLine,
        contactName: formData.contactName,
        timeZone: formData.timeZone,
        isLocation: formData.isLocation,
        latitude: formData.latitude ? Number(formData.latitude) : null,
        longitude: formData.longitude ? Number(formData.longitude) : null,
        radius: formData.radius ? Number(formData.radius) : 0,
        logo: {
          fileName: uploadedImageFiles.fileName,
          bucketName: uploadedImageFiles.bucketName,
          fileUrl: uploadedImageFiles.fileUrl,
          contentType: uploadedImageFiles.contentType,
          size: uploadedImageFiles.size,
        },
      };

      const tenant = await publicTenantRegistration(
        tenantPayload,
        createdUserId,
      );
      if (tenant) {
        Swal.fire(
          "Academy Created!",
          "Academy created successfully",
          "success",
        );
        setFormData(initialFormData);
        setErrors({});
        setStep(1);
        setCreatedUserId(null);
        setRegistrationSuccess(true);
      }
    } catch (error) {
      const message =
        error?.response?.data?.errorMessage ||
        error?.message ||
        "Something went wrong";
      Swal.fire("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };
  if (registrationSuccess) {
    return (
      <div
        className="container-fluid"
        style={{
          minHeight: "100vh",
          paddingTop: "80px",
          paddingBottom: "40px",
          background: "#f8f9fa",
        }}
      >
        <div className="row justify-content-center">
          <div className="col-lg-7 col-md-9">
            <Card className="border-0 shadow-lg">
              <Card.Body className="text-center p-5">
                <div
                  className="mx-auto mb-4 rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center"
                  style={{
                    width: "120px",
                    height: "120px",
                  }}
                >
                  <i
                    className="fa fa-check-circle text-success"
                    style={{ fontSize: "60px" }}
                  />
                </div>

                <h2 className="fw-bold text-success mb-3">
                  Academy Registration Successful
                </h2>

                <p className="text-muted fs-5 mb-4">
                  Your academy registration request has been submitted
                  successfully.
                </p>

                <div className="alert alert-warning text-center">
                  <h5 className="mb-2">⏳ Under Review</h5>

                  <p className="mb-0">
                    Your account is currently under verification. Once our team
                    reviews and approves your registration, you will be able to
                    log in and access the platform.
                  </p>
                </div>

                <div className="d-flex justify-content-center gap-3 mt-4">
                  <Button
                    variant="outline-success"
                    size="lg"
                    onClick={() =>
                      window.open(`${window.location.origin}/login`, "_blank")
                    }
                  >
                    Login To Website
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="">
        <div className="row justify-content-center">
          <div className="col-xl-12">
            <Card>
              <Card.Header>
                <h4 className="mb-0">Academy Self Registration</h4>
              </Card.Header>

              <Card.Body>
                <form onSubmit={handleSubmit} autoComplete="off">
                  {/* Decoy fields to prevent browser autofill */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-9999px",
                      left: "-9999px",
                      opacity: 0,
                      height: 0,
                      width: 0,
                      overflow: "hidden",
                    }}
                    aria-hidden="true"
                  >
                    <input
                      type="text"
                      name="fake_username_autofill"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                    <input
                      type="password"
                      name="fake_password_autofill"
                      tabIndex={-1}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="row">
                    <div className="col-12 mb-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-1">Academy Registration</h5>
                          <small className="text-muted">
                            {step === 1
                              ? "Step 1: Enter administrator account details."
                              : "Step 2: Now enter academy details and finish registration."}
                          </small>
                        </div>
                        <div>
                          <span
                            className={`badge bg-${step === 1 ? "primary" : "secondary"} me-2`}
                          >
                            1
                          </span>
                          <span
                            className={`badge bg-${step === 2 ? "primary" : "secondary"}`}
                          >
                            2
                          </span>
                        </div>
                      </div>
                    </div>

                    {step === 1 ? (
                      <>
                        <div className="col-12 mb-3">
                          <div className="card shadow-sm mb-4">
                            <div className="card-header py-3">
                              <h5 className="mb-1">Admin User Account</h5>
                              <small className="text-muted">
                                Create the admin user first, then continue to
                                academy details.
                              </small>
                            </div>
                            <div className="card-body">
                              <div className="row">
                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Full Name
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    name="adminName"
                                    autoComplete="off"
                                    className={`form-control ${errors.adminName ? "is-invalid" : ""}`}
                                    placeholder="Enter full name"
                                    value={formData.adminName}
                                    onChange={handleChange}
                                  />
                                  {errors.adminName && (
                                    <small className="text-danger">
                                      {errors.adminName}
                                    </small>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Mobile Number
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    name="adminMobile"
                                    autoComplete="off"
                                    className={`form-control ${errors.adminMobile ? "is-invalid" : ""}`}
                                    placeholder="Enter mobile number"
                                    value={formData.adminMobile}
                                    onChange={handleChange}
                                  />
                                  {errors.adminMobile && (
                                    <small className="text-danger">
                                      {errors.adminMobile}
                                    </small>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Email<span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="email"
                                    name="adminEmail"
                                    autoComplete="new-password"
                                    className={`form-control ${errors.adminEmail ? "is-invalid" : ""}`}
                                    placeholder="Enter email"
                                    value={formData.adminEmail}
                                    onChange={handleChange}
                                  />
                                  {errors.adminEmail && (
                                    <small className="text-danger">
                                      {errors.adminEmail}
                                    </small>
                                  )}
                                </div>

                                <div className="col-md-6 mb-3">
                                  <label className="form-label">
                                    Password<span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="password"
                                    name="password"
                                    autoComplete="new-password"
                                    placeholder="Enter password"
                                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                                    value={formData.password}
                                    onChange={handleChange}
                                  />
                                  {errors.password && (
                                    <small className="text-danger">
                                      {errors.password}
                                    </small>
                                  )}
                                </div>

                                <div className="col-sm-6">
                                  <div className="form-group">
                                    <label
                                      className="form-label"
                                      htmlFor="landLine"
                                    >
                                      Landline
                                    </label>
                                    <input
                                      id="landLine"
                                      name="landLine"
                                      autoComplete="off"
                                      placeholder="Enter Landline Number"
                                      type="text"
                                      className="form-control"
                                      value={formData.landLine}
                                      onChange={handleChange}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 text-end mt-3">
                          <Button
                            type="button"
                            variant="primary"
                            onClick={handleContinue}
                            disabled={loading}
                          >
                            Continue to Academy Details
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-12 mb-3">
                          <h5 className="border-bottom pb-2">
                            Academy Information
                          </h5>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Academy Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="tenantName"
                            className={`form-control ${errors.tenantName ? "is-invalid" : ""}`}
                            placeholder="Enter academy name"
                            value={formData.tenantName}
                            onChange={handleChange}
                          />
                          {errors.tenantName && (
                            <small className="text-danger">
                              {errors.tenantName}
                            </small>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Short Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="shortName"
                            placeholder="Enter short name"
                            className={`form-control ${errors.shortName ? "is-invalid" : ""}`}
                            value={formData.shortName}
                            onChange={handleChange}
                          />
                          {errors.shortName && (
                            <small className="text-danger">
                              {errors.shortName}
                            </small>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            academy Email<span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            name="companyEmail"
                            placeholder="Enter academy email"
                            className={`form-control ${errors.companyEmail ? "is-invalid" : ""}`}
                            value={formData.companyEmail}
                            onChange={handleChange}
                          />
                          {errors.companyEmail && (
                            <small className="text-danger">
                              {errors.companyEmail}
                            </small>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Academy Phone<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="companyPhone"
                            className={`form-control ${errors.companyPhone ? "is-invalid" : ""}`}
                            placeholder="Enter academy phone"
                            value={formData.companyPhone}
                            onChange={handleChange}
                          />
                          {errors.companyPhone && (
                            <small className="text-danger">
                              {errors.companyPhone}
                            </small>
                          )}
                        </div>
                        {/* Land Line */}
                        <div className="col-sm-6">
                          <div className="form-group">
                            <label className="form-label" htmlFor="landLine">
                              Land Line
                            </label>
                            <input
                              id="landLine"
                              name="landLine"
                              placeholder="Enter Landline Number"
                              type="text"
                              className="form-control"
                              value={formData.landLine}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div className="col-sm-6">
                          <div className="form-group">
                            <label className="form-label" htmlFor="contactName">
                              Contact Name<span className="text-danger">*</span>
                            </label>
                            <input
                              id="contactName"
                              name="contactName"
                              placeholder="Enter Contact Name"
                              type="text"
                              className={`form-control ${errors.contactName ? "is-invalid" : ""}`}
                              value={formData.contactName}
                              onChange={handleChange}
                            />
                            {errors.contactName && (
                              <small className="text-danger">
                                {errors.contactName}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="form-group">
                            <label className="form-label" htmlFor="timeZone">
                              Time Zone<span className="text-danger">*</span>
                            </label>
                            <Select
                              isSearchable={true}
                              options={timeZoneOptions}
                              placeholder="Select Time Zone"
                              className={`custom-react-select ${errors.timeZone ? "is-invalid" : ""}`}
                              value={
                                timeZoneOptions.find(
                                  (option) => option.value === formData.timeZone,
                                ) || null
                              }
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  timeZone: selected ? selected.value : "",
                                }))
                              }
                            />
                            {errors.timeZone && (
                              <small className="text-danger">
                                {errors.timeZone}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-sm-6">
                          <div className="form-group">
                            <label className="form-label">
                              Location Check<span className="text-danger">*</span>
                            </label>

                            <Select
                              options={locationOptions}
                              placeholder="Select Location Check"
                              className="custom-react-select"
                              value={locationOptions.find(
                                (option) => option.value === formData.isLocation,
                              )}
                              onChange={(selected) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  isLocation: selected.value,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="col-12 mb-3">
                          <label className="form-label">
                            Address<span className="text-danger">*</span>
                          </label>
                          <textarea
                            rows="3"
                            name="address"
                            placeholder="Enter address"
                            className={`form-control ${errors.address ? "is-invalid" : ""}`}
                            value={formData.address}
                            onChange={handleChange}
                          />
                          {errors.address && (
                            <small className="text-danger">
                              {errors.address}
                            </small>
                          )}
                        </div>

                        <div className="col-12 mt-2 mb-3">
                          <h5 className="border-bottom pb-2">
                            Academy Location Settings
                          </h5>
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Latitude<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="latitude"
                            className="form-control"
                            placeholder="Enter latitude"
                            value={formData.latitude}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Longitude<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            name="longitude"
                            className="form-control"
                            placeholder="Enter longitude"
                            value={formData.longitude}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Radius (Meters)<span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            name="radius"
                            placeholder="Enter radius (meters)"
                            className="form-control"
                            value={formData.radius}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-sm-12">
                          <div className="form-group mb-3">
                            <label className="form-label fw-semibold">
                              Academy Logo
                            </label>

                            <div
                              className="d-flex align-items-center"
                              style={{
                                gap: "12px",
                                minHeight: "70px",
                              }}
                            >
                              {/* Logo preview - only shown after upload */}
                              {uploadedImageFiles?.fileUrl && (
                                <div
                                  style={{
                                    width: "68px",
                                    height: "68px",
                                    border: "1px solid #e2e6ea",
                                    borderRadius: "8px",
                                    backgroundColor: "#f8f9fa",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                  }}
                                >
                                  <img
                                    src={uploadedImageFiles.fileUrl}
                                    alt="Academy Logo"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                      padding: "5px",
                                    }}
                                  />
                                </div>
                              )}

                              {/* Upload area */}
                              <div>
                                <Button
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  onClick={() => setShowImageCropModal(true)}
                                  style={{
                                    borderRadius: "6px",
                                    padding: "7px 12px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                  }}
                                >
                                  <i
                                    className="fa fa-upload"
                                    style={{
                                      marginRight: "6px",
                                    }}
                                  />

                                  {uploadedImageFiles?.fileUrl
                                    ? "Change Logo"
                                    : "Upload Logo"}
                                </Button>

                                <div
                                  className="text-muted"
                                  style={{
                                    fontSize: "11px",
                                    marginTop: "4px",
                                  }}
                                >
                                  PNG, JPG or WEBP • Max 10 MB
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-12 text-start mt-3">
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={handleBack}
                            disabled={loading}
                            className="me-2"
                          >
                            Back
                          </Button>
                          <Button
                            type="submit"
                            variant="primary"
                            disabled={loading}
                          >
                            {loading ? "Registering..." : "Register Academy"}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </form>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      <PublicImageCropModal
        show={showImageCropModal}
        onClose={() => setShowImageCropModal(false)}
        existingImage=""
        onUploadSuccess={(response) => {
          // Only after successful upload
          // show image on page
          setUploadedImageFiles(response);

          // Close crop modal
          setShowImageCropModal(false);
        }}
      />
    </>
  );
};

export default TenantRegistration;
